import { CSSObject } from "@emotion/react";
import {
  constants,
  mediaQueryStyles,
  typographyStyles,
} from "@newsela/angelou";

const { fontSize } = typographyStyles;
const { mediaQuery: breakpoint } = mediaQueryStyles;
const { Breakpoints } = constants.breakpoints;
const { sm, md, lg } = Breakpoints;

const RESOURCE_DRAWER_WIDTH = 569; // For 769-1024 screens
const OUTLINE_TREE_EXPAND_BUTTON_HEIGHT = 40; // For < md screens

export const mainContainerCss = (
  isResourcePanelHidden: boolean,
): CSSObject => ({
  position: "relative",
  display: "grid",
  overflow: "hidden",
  minHeight: "100vh",
  alignContent: "start",
  // xs and sm: single column (covers 0 -> md-1)
  gridTemplateColumns: "1fr",
  gridTemplateRows: isResourcePanelHidden ? "auto auto" : "auto auto auto",
  transition: "grid-template-columns 0.3s ease-in-out",

  // md: 3 columns (left / main / right)
  [breakpoint.downTo(md)]: {
    overflow: "visible",
    gridTemplateColumns: isResourcePanelHidden
      ? "220px minmax(0, 1fr)"
      : "220px minmax(0, 1fr) 320px",
    gridTemplateRows: "1fr",
  },

  // lg: 3 columns (left / main / right)
  [breakpoint.downTo(lg)]: {
    gridTemplateColumns: isResourcePanelHidden
      ? "280px minmax(0, 1fr)"
      : "280px minmax(0, 1fr) 390px",
  },
});

export const outlineWrapperCss: CSSObject = {
  [breakpoint.downTo(md)]: {
    position: "sticky",
    top: 0,
    height: "100vh",
    zIndex: 1,
  },
};

/** =========================
 *  Main content (center) styles
 *  =========================
 */

export const centerContentPaneCss: CSSObject = {
  padding: "20px",
  backgroundColor: constants.colors.ui.white[700],

  [breakpoint.upTo(md)]: {
    paddingBottom: "64px",
  },
};

/** =========================
 *  Outline tree (left panel) styles
 *  =========================
 */

// Sticky toggle bar that sits at the very top on < md; hidden on ≥ md.
export const outlineToggleButtonCss: CSSObject = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 16px",
  borderRadius: 0,
  backgroundColor: constants.colors.ui.core[500],
  color: constants.colors.ui.white[100],
  boxShadow: "0 1px 0 rgba(0,0,0,0.06)",
  cursor: "pointer",
  ...fontSize.textM,
  fontWeight: 700,
  minHeight: OUTLINE_TREE_EXPAND_BUTTON_HEIGHT,

  ":hover": {
    backgroundColor: constants.colors.ui.core[500],
  },

  // Hide this bar on ≥ md (desktop keeps the full left column)
  [breakpoint.downTo(md)]: {
    display: "none",
  },
};

export const outlineToggleLabelCss: CSSObject = {
  display: "inline-flex",
  alignItems: "center",
};

export const chevronIconCss = (isOpen: boolean): CSSObject => ({
  width: 20,
  height: 20,
  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
  transition: "transform 180ms ease",
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
});

export const outlineContainerCss = (isOpen: boolean): CSSObject => ({
  /* --- Screens < md: overlay the content sits on top --- */
  position: "absolute",
  left: 0,
  right: 0,
  // Place the panel just below the sticky toggle so it never covers the button.
  top: OUTLINE_TREE_EXPAND_BUTTON_HEIGHT + 8,
  width: "100%",
  height: "100%",
  zIndex: 13,
  background: constants.colors.ui.neutral[25],
  boxShadow: "0 12px 24px rgba(0,0,0,0.2)",
  borderBottom: `1px solid ${constants.colors.ui.neutral[100]}`,
  maxHeight: isOpen ? "70vh" : 0,
  overflowY: "scroll",
  transition: "max-height 220ms ease",
  pointerEvents: isOpen ? "auto" : "none",

  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },

  /* --- Screens ≥ md: reset to left column behavior --- */
  [breakpoint.downTo(md)]: {
    position: "static",
    top: "auto",
    left: "auto",
    right: "auto",
    width: "auto",
    zIndex: "auto",
    background: "transparent",
    boxShadow: "none",
    borderBottom: "none",
    borderRadius: 0,

    maxHeight: "none",
    overflow: "visible",
    transition: "none",
    padding: 0,
    pointerEvents: "auto",
  },
});

/** =========================
 *  Resource panel (right panel) styles
 *  =========================
 */

export const resourceRegionCss: CSSObject = {
  position: "relative",
};

// Bottom sticky tab button (< md)
export const resourceTabButtonCss: CSSObject = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  height: 70,
  margin: "0 auto",
  borderRadius: "12px 12px 0 0",
  boxShadow: "0px -3px 10px rgba(0, 0, 0, 0.25)",
  backgroundColor: constants.colors.ui.white[700],
  zIndex: 4,
  cursor: "pointer",

  "&:hover": {
    background: constants.colors.ui.white[700],
  },

  [breakpoint.downTo(md)]: {
    display: "none",
  },

  [breakpoint.downTo(sm)]: {
    width: RESOURCE_DRAWER_WIDTH,
  },

  [breakpoint.upTo(sm)]: {
    width: "100%",
  },
};

export const resourceTabInnerContentCss: CSSObject = {
  ...fontSize.textL,
  fontWeight: 700,
  display: "flex",
  width: "100%",
  padding: 24,
  justifyContent: "space-between",
  alignItems: "center",
};

export const viewResourcesIconCss: CSSObject = {
  width: 24,
  height: 24,
};

export const resourceContainerCss = (isOpen: boolean): CSSObject => ({
  // Drawer mode (default for < md)
  display: "flex",
  flexDirection: "column",
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  margin: "0 auto",
  maxHeight: "85vh",
  height: "min(85vh, 720px)",
  width: RESOURCE_DRAWER_WIDTH,
  transform: isOpen ? "translateY(0)" : "translateY(100%)",
  transition: "transform 240ms ease-out",
  zIndex: 5,
  backgroundColor: constants.colors.ui.white[700],
  borderRadius: "12px 12px 0 0",
  overflowY: "scroll",
  boxShadow: "0 -8px 24px rgba(0,0,0,0.2)",

  // Sidebar mode (≥ md)
  [breakpoint.downTo(md)]: {
    position: "sticky",
    top: 0,
    height: "100vh",
    maxHeight: "none",
    transform: "none",
    transition: "none",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    boxShadow: "none",
    borderLeft: `1px solid ${constants.colors.ui.neutral[100]}`,
    width: "auto",
    overflowY: "auto",
  },

  [breakpoint.upTo(sm)]: {
    width: "100%",
  },
});
